import { useState, MouseEvent, useRef, useEffect, useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  Audio,
  AudioRenderPropsType,
} from '@insights_module/components/episode_details_screen/Audio';
import { GarmHeatMap } from '@insights_module/components/heat-map/GarmHeatMap';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { Loader } from '@components/Loader';
import { Layout } from '@insights_module/components/Layout';
import { convertSecondsToHMS } from '@utils/dateUtils';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { EpisodeGarmFilter, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import {
  scrollToParagraph,
  audioTimeToFixedNum,
  scrollToSegment,
  findSegmentByStartTime,
  findNearestTimeSegment,
  findParagraphByStartTime,
  findNearestTimeParagraph,
} from '@insights_module/utils/episodesUtils';
import { PlayIconFill } from '@icons/PlayIconFill';
import { PauseIcon } from '@icons/PauseIcon';
import { CaretIconUp } from '@icons/CaretIconUp';
import { CaretIcon } from '@icons/CaretIcon';
import { HeatMapIcon } from '@icons/HeatMapIcon';
import { CollapseIcon } from '@icons/CollapseIcon';
import { ExpandIcon } from '@icons/ExpandIcon';
import { AUDIO_ERROR_TYPE, useEpisodeUrl } from '@insights_module/hooks/useEpisodeUrl';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const Container = tw.div`sticky bottom-0 flex`;
const InfoBox = tw.div`flex justify-between items-center flex-grow px-[11px] py-2`;
const TimeBox = tw.div`flex justify-start w-24 text-xs text-text-and-icon-muted`;
const NotificationBox = tw.div`flex justify-center items-center w-auto text-xs text-text-and-icon-muted whitespace-nowrap`;
const LoadingOverlay = tw.div`absolute top-0 left-0 h-full w-full bg-neutral-background z-10 flex justify-center items-center`;
const Controls = tw.div`flex items-center justify-between border-t border-gray-1 p-2 h-11 w-full`;
const PlayPauseButton = tw.button`bg-accent-2 rounded-full w-[31px] h-[31px] min-w-[31px] min-h-[31px] flex items-center justify-center mr-4 disabled:opacity-75 transition-all duration-200`;
const ProgressBarWrapper = tw.div`h-2 bg-sec-1 w-full transition-all duration-200 cursor-pointer rounded`;
const Progress = tw.div`h-2 bg-accent-2 relative rounded-lg`;
const Seeker = tw.div`inline-block w-0 h-0 rounded-full bg-accent-2 absolute right-0 mt-1 transition-all duration-200 shadow group-hover:w-4 group-hover:h-4  group-hover:right-[-1px] group-hover:mt-[-4px]`;
const CollapsibleWrapper = tw.div`transition-all duration-200 overflow-hidden`;
const HeatmapToggler = tw.button`w-auto h-full text-[10px] px-2 whitespace-nowrap bg-accent-2 rounded-md text-button`;
const PlayerTogglerButton = tw(
  HeatmapToggler
)`bg-gray-3 flex items-center justify-center w-6 h-6 p-0`;
const MinimizedPlayerWrapper = tw.div`bg-gray-2 flex flex-col items-center justify-center p-[6px] border border-solid border-gray-1 rounded-lg`;
const TogglersContainer = tw.div`flex items-center justify-center h-6`;

type PlayPauseIconPropsType = {
  isPlaying: boolean;
  isLoading: boolean;
};

type ProgressBarPropsType = {
  progressPercentage: number;
  isHeatMapExpanded: boolean;
  isDragging: boolean;
};

const PlayPauseIcon = ({ isPlaying, isLoading }: PlayPauseIconPropsType): JSX.Element => {
  if (isLoading) return <Loader size='sm' theme='dark' />;

  return isPlaying ? (
    <PauseIcon tw='fill-text-and-icon-invert' data-testid='audio-player-pause-icon' />
  ) : (
    <PlayIconFill tw='stroke-text-and-icon-invert h-[16px]' data-testid='audio-player-play-icon' />
  );
};

const ProgressBar = forwardRef<HTMLDivElement, ProgressBarPropsType>(
  ({ progressPercentage, isHeatMapExpanded, isDragging }, ref): JSX.Element => {
    return (
      <ProgressBarWrapper
        ref={ref}
        id='insights-audio-progressbarr'
        className='group'
        css={[isHeatMapExpanded ? tw`w-[calc(100% - 181px)]` : tw`w-full`]}>
        <Progress style={{ width: `${progressPercentage}%` }}>
          <Seeker css={[isDragging && tw`w-4 h-4 -mt-1`]} />
        </Progress>
      </ProgressBarWrapper>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

const PlayerToggler = ({
  onClick,
  isExpanded,
  ...rest
}: {
  onClick: () => void;
  isExpanded: boolean;
}) => {
  return (
    <PlayerTogglerButton data-testid='player-toggler' onClick={onClick} {...rest}>
      {isExpanded ? (
        <CollapseIcon backgroundFillColor='transparent' tw='w-6 h-6 fill-text-and-icon' />
      ) : (
        <ExpandIcon tw='fill-text-and-icon' />
      )}
    </PlayerTogglerButton>
  );
};

// Component receives props from the renderProp HOC, and those props are initialized inside JSX.
// Because of that we had to define this component separately so we could use those props
// in lifecycle hooks that we can't call from within JSX. [e.g. useEffect]
const Player = ({
  audio,
  episodeGarmResults,
  heatmapGarmResults,
}: {
  audio: AudioRenderPropsType;
  episodeGarmResults: EpisodeGarmFilter[];
  heatmapGarmResults: {
    id: number;
    start: number;
    end: number;
    category: string;
    risk: GarmRiskLevelType;
  }[][];
}): JSX.Element => {
  const { t } = useTranslation();
  const progressBarRef = useRef<HTMLDivElement>(null);
  const togglerRef = useRef<HTMLButtonElement>(null);
  const [isGarmHeatMapVisible, setGarmHeatMapVisible] = useState<boolean>(false);
  const [shouldContinuePlaying, setShouldContinuePlaying] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const {
    currentAudio,
    isAudioPlaying,
    setCurrentAudio,
    setIsAudioPlaying,
    setSearchTerm,
    setSelectedEntity,
    setSelectedSegment,
    setSelectedGarmLevel,
    setSelectedGarmCategory,
    selectedChapterTime,
    setSelectedChapterTime,
    activeView,
  } = useEpisodeDetailsContext();
  const { selectedSegment, segmentsWithParagraphs } = useEpisodeSegment();
  const [isDragging, setIsDragging] = useState(false);
  const {
    currentTime,
    duration,
    isPlaying,
    isBuffering,
    isLoading,
    progressPercentage,
    setCurrentTime,
    toggle,
    play,
    pause,
  } = audio;
  const { error } = useEpisodeUrl();

  const shouldShowHeatMap = useMemo(() => activeView === 'garm_brand_safety', [activeView]);

  // Current audio state. Track audio currentTime and return current segment and paragraph
  const _current = useMemo(() => {
    // if audio duration is not in sync with transcript duration, don't auto scroll
    if (error?.cause === AUDIO_ERROR_TYPE.AUDIO_DURATION) {
      return { segment: null, paragraph: null };
    }

    const segment = segmentsWithParagraphs?.find((item) => {
      return audioTimeToFixedNum(Number(item.startTime)) === audioTimeToFixedNum(currentTime);
    });

    const paragraph = currentAudio.segment?.paragraphData?.find((item) => {
      return audioTimeToFixedNum(Number(item.startTime)) === audioTimeToFixedNum(currentTime);
    });

    return { segment, paragraph };
  }, [currentTime]);

  const handleToggleAudio = () => {
    setIsAudioPlaying(!isPlaying);
    toggle();
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLDivElement;
    const isProgressBarRef =
      target === progressBarRef.current ||
      target.parentElement === progressBarRef.current ||
      target.parentElement?.parentElement === progressBarRef.current;

    if (target && isProgressBarRef) {
      if (isPlaying) {
        setShouldContinuePlaying(true);
      }
      setIsDragging(true);
      setIsAudioPlaying(false);
      handleProgressBarChange(getProgressBarTime(duration, e as MouseEvent));
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) {
      return;
    }

    handleProgressBarChange(getProgressBarTime(duration, e as MouseEvent));
  };

  const handleMouseUp = (): void => {
    if (isDragging) {
      shouldContinuePlaying ? setIsAudioPlaying(true) : setIsAudioPlaying(false);

      // Reset states
      setIsDragging(false);
      setShouldContinuePlaying(false);
    }
  };

  const handleProgressBarChange = (val: number) => {
    setSearchTerm('');
    setIsAudioPlaying(true);
    setSelectedEntity(null);
    setSelectedSegment(null);
    setSelectedGarmCategory(null);
    setSelectedGarmLevel(null);

    const segment =
      findSegmentByStartTime(segmentsWithParagraphs, val) ||
      findNearestTimeSegment(segmentsWithParagraphs, val);
    const paragraph =
      findParagraphByStartTime(currentAudio.segment?.paragraphData, val) ||
      findNearestTimeParagraph(segment?.paragraphData, val);

    if (segment?.id) {
      setCurrentTime(Number(segment?.startTime));
      scrollToSegment(segment?.id);
    } else if (paragraph) {
      setCurrentTime(Number(paragraph?.startTime));
      scrollToParagraph(Number(paragraph?.startTime));
    }

    setCurrentAudio({ segment, paragraph });
  };

  const getProgressBarTime = (duration: number, e?: MouseEvent): number => {
    if (!progressBarRef.current) return 0;

    const progressBarRect = progressBarRef.current?.getBoundingClientRect();
    const clickPosition = e?.clientX;
    const progressBarOffset = clickPosition ? clickPosition - progressBarRect.left : 0;

    return (progressBarOffset / progressBarRect.width) * duration;
  };

  const handleHeatMapToggle = () => {
    setGarmHeatMapVisible((isGarmHeatMapVisible) => !isGarmHeatMapVisible);
  };

  const handleToggleExpandPlayer = () => {
    setIsExpanded((isExpanded) => !isExpanded);
  };

  useEffect(() => {
    setCurrentAudio(_current);

    if (_current.paragraph) {
      scrollToParagraph(Number(_current.paragraph.startTime));
      return;
    }

    if (_current.segment) {
      scrollToParagraph(Number(_current.segment.startTime));
    }
  }, [_current]);

  useEffect(() => {
    isAudioPlaying && !isDragging ? play() : pause();
  }, [isAudioPlaying, isDragging]);

  useEffect(() => {
    if (selectedSegment?.startTime) {
      setCurrentTime(selectedSegment?.startTime);
    }
  }, [selectedSegment]);

  useEffect(() => {
    if (selectedChapterTime) {
      setCurrentTime(selectedChapterTime);
      setSelectedChapterTime(null);
      setIsAudioPlaying(true);
    }
  }, [selectedChapterTime]);

  return (
    <Container
      data-testid='audio-player-container'
      onMouseDown={(e) => handleMouseDown(e as MouseEvent<HTMLDivElement>)}
      onMouseMove={(e) => handleMouseMove(e as MouseEvent<HTMLDivElement>)}
      onMouseUp={handleMouseUp}
      css={[!isExpanded && tw`justify-end`]}>
      {!isExpanded ? (
        error?.cause === AUDIO_ERROR_TYPE.INVALID_URL ? (
          <MinimizedPlayerWrapper>
            <PlayerToggler
              onClick={handleToggleExpandPlayer}
              isExpanded={isExpanded}
              tw='p-1 h-full'
            />
          </MinimizedPlayerWrapper>
        ) : (
          <MinimizedPlayerWrapper className='group' data-testid='minimized-audio-player'>
            <PlayerToggler
              tw='h-0 overflow-hidden group-hover:mb-3 group-hover:h-6 group-hover:w-6 transition-all duration-200 bg-gray-3'
              onClick={handleToggleExpandPlayer}
              isExpanded={isExpanded}
            />
            <PlayPauseButton
              data-testid='play-pause-toggler'
              onClick={handleToggleAudio}
              disabled={isBuffering}
              tw='m-0'>
              <PlayPauseIcon isPlaying={isPlaying} isLoading={isBuffering} />
            </PlayPauseButton>
          </MinimizedPlayerWrapper>
        )
      ) : (
        <Layout.Card tw='shadow-quaternary' data-testid='full-audio-player'>
          <InfoBox>
            <TimeBox>
              <span>{convertSecondsToHMS(currentTime)}</span>
              <span tw='px-1'>/</span>
              <span>{convertSecondsToHMS(duration)}</span>
            </TimeBox>

            <TogglersContainer>
              {!error ? (
                heatmapGarmResults.length > 0 &&
                shouldShowHeatMap && (
                  <HeatmapToggler ref={togglerRef} onClick={handleHeatMapToggle}>
                    <HeatMapIcon tw='stroke-text-and-icon-invert mr-1 inline' />
                    <span tw='whitespace-nowrap text-text-and-icon-invert'>
                      {t('common.heatmap')}
                    </span>
                    {isGarmHeatMapVisible ? (
                      <CaretIcon tw='fill-text-and-icon-invert inline-block w-2 ml-1' />
                    ) : (
                      <CaretIconUp tw='stroke-text-and-icon-invert inline-block w-2 ml-1 -mt-1' />
                    )}
                  </HeatmapToggler>
                )
              ) : (
                <NotificationBox>{error?.message}</NotificationBox>
              )}
              <PlayerToggler
                onClick={handleToggleExpandPlayer}
                isExpanded={isExpanded}
                tw='p-0 h-full ml-2'
              />
            </TogglersContainer>
          </InfoBox>

          {error?.cause === AUDIO_ERROR_TYPE.INVALID_URL ? null : (
            <>
              <CollapsibleWrapper
                // We need to set some value for max-height to enable css animation.
                // With 50vh height, we don't allow the component to overlap with the rest of the UI.
                css={[isGarmHeatMapVisible ? tw`h-auto max-h-[50vh]` : tw`max-h-[0]`]}>
                <GarmHeatMap
                  episodeGarmResults={episodeGarmResults}
                  heatmapData={heatmapGarmResults}
                  duration={duration}
                  onTimeUpdate={(val) => setCurrentTime(val as number)}
                />
              </CollapsibleWrapper>

              <Controls>
                <PlayPauseButton
                  onClick={handleToggleAudio}
                  disabled={isBuffering}
                  data-testid='play-pause-toggler'>
                  <PlayPauseIcon isPlaying={isPlaying} isLoading={isBuffering} />
                </PlayPauseButton>

                <ProgressBar
                  ref={progressBarRef}
                  isHeatMapExpanded={isGarmHeatMapVisible}
                  progressPercentage={progressPercentage}
                  isDragging={isDragging}
                />
              </Controls>
            </>
          )}
        </Layout.Card>
      )}

      {isLoading && (
        <LoadingOverlay data-testid='audio-player-loader'>
          <Loader size='md' theme='dark' />
        </LoadingOverlay>
      )}
    </Container>
  );
};

export const AudioPlayer = ({ url }: { url: string | undefined | null }) => {
  const { episodeSegments } = useEpisodeSegment();
  const { selectedEpisode } = useInsightsContext();
  const { getGarmForEpisode } = useEpisodeDetailsData();
  const garm = getGarmForEpisode(selectedEpisode);
  const { results: episodeGarmResults } = garm;

  const heatmapGarmResults = useMemo(
    () =>
      episodeSegments
        // Filter segments that are unrelated to all garm categories.
        .filter((segment) =>
          Object.values(segment.garm).some(
            (risk) => risk.toLowerCase() !== GARM_RISK_LEVEL.unrelated
          )
        )
        // Map filtered results.
        .map((segment) =>
          // Map garm results for each segment. Each objects in array will contain the name of the
          // garm category, risk level, and additional data about the segment in which it is located.
          Object.entries(segment.garm)
            .filter(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ([_, val]) => val.toLowerCase() !== GARM_RISK_LEVEL.unrelated
            )
            .map((garm) => ({
              id: segment.id,
              start: segment.startTime,
              end: segment.endTime,
              category: garm[0],
              risk: garm[1],
            }))
        ),

    [episodeSegments]
  );

  if (!url) return null;

  return (
    <Audio
      src={url}
      renderContent={(audio: AudioRenderPropsType) => (
        <Player
          audio={audio}
          episodeGarmResults={episodeGarmResults as EpisodeGarmFilter[]}
          heatmapGarmResults={heatmapGarmResults}
        />
      )}
    />
  );
};
